import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';

const AuthCallback = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash ? window.location.hash.substring(1) : '';
    if (!hash) return;
    const params = new URLSearchParams(hash);

    const error = params.get('error');
    const error_description = params.get('error_description');
    if (error) {
      setErrorMsg(decodeURIComponent(error_description || error));
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    if (access_token && refresh_token) {
      supabase.auth.setSession({ access_token, refresh_token }).then(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      });
    }
  }, []);
  
  useEffect(() => {
    if (!loading && !user) {
      navigate('/sign-in');
    } else if (!loading && user) {
      // User is authenticated, now redirect based on role
      const redirectBasedOnRole = async () => {
        try {
          const { data: roles, error } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id);

          if (error) throw error;

          if (roles && roles.length > 0) {
            const roleHierarchy = ['admin', 'broker', 'seller', 'buyer'] as const;
            const userRoles = roles.map((r: { role: string }) => r.role);
            const primaryRole = roleHierarchy.find((role) => userRoles.includes(role as any));

            switch (primaryRole) {
              case 'admin':
                navigate('/admin-setup');
                break;
              case 'broker':
                navigate('/broker-portal');
                break;
              case 'buyer':
                // Send new buyers to the intake questionnaire first
                navigate('/buyer-intake');
                break;
              case 'seller':
              default:
                navigate('/dashboard');
            }
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          navigate('/dashboard');
        }
      };

      redirectBasedOnRole();
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Verifying your account...</h1>
            <p className="text-muted-foreground">Please wait while we complete your email verification.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Verification link expired</h1>
            <p className="text-muted-foreground">{errorMsg}. Please request a new verification email from the Sign In page.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Account verified successfully!</h1>
          <p className="text-muted-foreground">Redirecting you to your dashboard...</p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AuthCallback;